import React from 'react';

import { ConsoleShell } from '../../../design-system/components/shells';
import {
  Button,
  Drawer,
  Input,
  PageHeader,
  Pagination,
  Rating,
  Select,
  Table,
  Toolbar,
} from '../../../design-system/components/desktop';
import {
  ActionRow,
  ConfirmDialog,
  ConsoleCell,
  ConsoleDenied,
  ConsoleEmpty,
  ConsoleError,
  ConsoleStack,
  DetailGallery,
  DetailList,
  DetailRow,
  DetailSection,
  ReasonBlock,
  RejectReasonForm,
  ResolvedNote,
  StatusPill,
  ThumbStrip,
} from '../../../design-system/patterns/console';
import { formatCurrency } from '../../../utils/common';

/**
 * Marketplace listing queue, the view. Pure.
 *
 * WHAT DIED HERE
 * --------------
 * - `Pagination.jsx`, the `2`-suffixed twin of the one in `AdminSellers/`. Both
 *   injected the SAME raw `<style>` block with `color: #fff` hardcoded; the
 *   suffix existed only so the two copies would not fight over `.nav-btn` in
 *   the global scope. Two files, one bug, both gone.
 * - `FIlters.jsx`, whose filter panel was a dropdown of bare `<select>` and
 *   `<input>` elements positioned absolutely, and which owned a page counter
 *   that disagreed with the one the API returned.
 * - `data.table.jsx` and its hand-rolled skeleton.
 * - `components/UI/MarketPlaceLookup/`, the detail panel, with its gradient
 *   approve button, a red reject button, a `⭐` emoji rating and the same
 *   hardcoded `R89.99` "Payable" literal that `SellerDetailsCard` carried.
 *
 * TWO THINGS DELIBERATELY NOT CARRIED OVER
 * ----------------------------------------
 * 1. The `R89.99` payable figure. It was a literal with nothing behind it.
 * 2. The `⭐ 4.5 (12)` rating string. It is now `Rating`, which draws ink stars
 *    with a clipped fill and reads its denominator explicitly. The value comes
 *    from `seller_rating_stats.rating_out_of_5`, so `outOf` is 5 and stated.
 *
 * THE FILTERS ARE REAL. `adminMarketPlace.getListings` strips empty values and
 * forwards the rest, and `status`, `card_name` and `store_name` were the three
 * the old panel sent, so all three survive unchanged.
 *
 * ONE OVERLAY AT A TIME
 * ---------------------
 * Opening the confirmation or the reason form CLOSES the drawer, and cancelling
 * either one brings it back. That is the upstream template's rule verbatim:
 * "the drawer steps aside so the reason form is the only thing asking for a
 * decision". It is not only a taste call. `Drawer` and `Modal` share
 * `--ds-z-modal` and both install a focus trap through `useModalLayer`, so two
 * open at once means two traps fighting over Tab and an Escape that closes both.
 * `open` is derived, not a second piece of state, so the two cannot disagree.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function MarketPlaceDashboardView({
  listings = [],
  loading = false,
  error,
  onRetry,
  denied = false,

  filters = { status: '', card_name: '', store_name: '' },
  onFilterChange,
  onClearFilters,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  selected,
  onOpen,
  onClose,
  imageIndex = 0,
  onPrevImage,
  onNextImage,

  onApprove,
  onReject,
  actionBusy = false,
  confirmApproveOpen = false,
  onConfirmApproveOpen,
  onConfirmApproveClose,
  rejectOpen = false,
  onRejectOpen,
  onRejectClose,
}) {
  const columns = [
    {
      key: 'card_name',
      header: 'Card',
      render: (row) => (
        <ConsoleCell
          title={row.card_name || 'Unnamed card'}
          sub={row.condition}
          initials={(row.card_name || 'C').slice(0, 2)}
        />
      ),
    },
    {
      key: 'store_name',
      header: 'Seller',
      render: (row) => (
        <ConsoleCell
          avatar={false}
          title={row.seller?.store_name || 'No store name'}
          sub={row.seller?.full_name}
        />
      ),
    },
    { key: 'price', header: 'Price', numeric: true, render: (row) => formatCurrency(row.price) },
    { key: 'quantity', header: 'Qty', numeric: true, render: (row) => row.quantity ?? 0 },
    {
      key: 'images',
      header: 'Images',
      render: (row) => <ThumbStrip images={row.images || []} alt={row.card_name || 'Listing image'} />,
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
    {
      key: 'created_at',
      header: 'Listed',
      muted: true,
      render: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString('en-ZA') : 'Unknown',
    },
  ];

  const shell = (children) => (
    <ConsoleShell
      variant="admin"
      title="Marketplace"
      breadcrumbs={[{ label: 'Catalogue' }, { label: 'Marketplace' }]}
      showSearch={false}
    >
      {children}
    </ConsoleShell>
  );

  if (denied) return shell(<ConsoleDenied />);

  const chips = [];
  if (filters.status) {
    chips.push({
      id: 'status',
      label: `Status: ${STATUS_OPTIONS.find((o) => o.value === filters.status)?.label || filters.status}`,
      onRemove: () => onFilterChange('status', ''),
    });
  }
  if (filters.store_name) {
    chips.push({
      id: 'store_name',
      label: `Store: ${filters.store_name}`,
      onRemove: () => onFilterChange('store_name', ''),
    });
  }

  const isPending = String(selected?.status || '').toLowerCase() === 'pending';
  const rating = Number(selected?.seller_rating_stats?.rating_out_of_5 || 0);

  return shell(
    <>
      <ConsoleStack>
        <PageHeader
          title="Listing queue"
          sub={
            loading
              ? 'Loading listings'
              : `${listings.length} shown of ${total} ${total === 1 ? 'listing' : 'listings'}`
          }
        />

        <Table
          columns={columns}
          data={error ? [] : listings}
          rowKey="id"
          loading={loading}
          onRowClick={(row) => onOpen(row)}
          emptyState={
            error ? (
              <ConsoleError error={error} onRetry={onRetry} />
            ) : (
              <ConsoleEmpty
                filtered={Boolean(filters.status || filters.card_name || filters.store_name)}
                /* Without an explicit slug this inherits ConsoleEmpty's
                   `no-orders-yet` default and draws an ORDERS ledger over a
                   sentence about listings. It renders artwork either way,
                   which is what made it the hardest one to catch. */
                illustration="no-listings-yet"
                title="No listings yet"
                text="Listings appear here as sellers submit cards for review."
                filteredText="No listing matches the status, card or store you filtered by. Remove one of them and the listings come back."
                onClear={onClearFilters}
              />
            )
          }
          rowActions={(row) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onOpen(row);
              }}
            >
              Review
            </Button>
          )}
          toolbar={
            <Toolbar
              search={filters.card_name}
              onSearchChange={(value) => onFilterChange('card_name', value)}
              searchPlaceholder="Filter by card name"
              searchLabel="Filter listings by card name"
              chips={chips}
              onClearFilters={chips.length ? onClearFilters : undefined}
              filters={
                <>
                  <Select
                    options={STATUS_OPTIONS}
                    value={filters.status}
                    onChange={(value) => onFilterChange('status', value)}
                    label="Status"
                    placeholder="All statuses"
                  />
                  <Input
                    value={filters.store_name}
                    placeholder="Store name"
                    aria-label="Filter listings by store name"
                    onChange={(event) => onFilterChange('store_name', event.target.value)}
                  />
                </>
              }
            />
          }
          pagination={
            pageCount > 1 ? (
              <Pagination
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                totalItems={total}
                itemNoun="listings"
                onPageChange={onPageChange}
              />
            ) : null
          }
        />
      </ConsoleStack>

      <Drawer
        open={Boolean(selected) && !confirmApproveOpen && !rejectOpen}
        onClose={onClose}
        kicker="Listing"
        title={selected?.card_name || 'Listing'}
        footer={
          isPending ? (
            <ActionRow>
              <Button variant="ghost" onClick={onRejectOpen} disabled={actionBusy}>
                Reject listing
              </Button>
              <Button variant="primary" onClick={onConfirmApproveOpen} disabled={actionBusy}>
                Approve listing
              </Button>
            </ActionRow>
          ) : (
            <ResolvedNote>
              {String(selected?.status || '').toLowerCase() === 'approved'
                ? 'This listing is live on the marketplace.'
                : 'This listing was rejected and is not visible to buyers.'}
            </ResolvedNote>
          )
        }
      >
        {selected ? (
          <>
            <DetailSection>
              <DetailGallery
                images={selected.images || []}
                index={imageIndex}
                onPrev={onPrevImage}
                onNext={onNextImage}
                alt={selected.card_name || 'Listing image'}
              />
            </DetailSection>

            <DetailSection>
              <DetailList>
                <DetailRow label="Status" value={<StatusPill status={selected.status} />} keepEmpty />
                <DetailRow label="Condition" value={selected.condition} />
                <DetailRow label="Price" value={formatCurrency(selected.price)} />
                <DetailRow label="Quantity" value={selected.quantity} />
                <DetailRow
                  label="Listed"
                  value={
                    selected.created_at
                      ? new Date(selected.created_at).toLocaleDateString('en-ZA')
                      : undefined
                  }
                />
              </DetailList>
            </DetailSection>

            <DetailSection title="Seller">
              <DetailList>
                <DetailRow label="Store" value={selected.seller?.store_name} />
                <DetailRow label="Seller" value={selected.seller?.full_name} />
                <DetailRow
                  label="Rating"
                  value={
                    <Rating
                      value={rating}
                      outOf={5}
                      size="sm"
                      count={selected.seller_rating_stats?.review_count}
                    />
                  }
                  keepEmpty
                />
              </DetailList>
            </DetailSection>

            {(selected.rejection_reason || selected.marks) && (
              <DetailSection>
                <ReasonBlock>{selected.rejection_reason || selected.marks}</ReasonBlock>
              </DetailSection>
            )}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmApproveOpen}
        onClose={onConfirmApproveClose}
        onConfirm={onApprove}
        title="Approve listing"
        confirmLabel="Approve listing"
        busy={actionBusy}
      >
        <p>
          <strong>{selected?.card_name}</strong> goes live on the marketplace at{' '}
          <strong>{formatCurrency(selected?.price)}</strong> immediately.
        </p>
      </ConfirmDialog>

      <RejectReasonForm
        open={rejectOpen}
        onClose={onRejectClose}
        onSubmit={onReject}
        title="Reject listing"
        submitLabel="Reject listing"
        initialReason={selected?.rejection_reason || selected?.marks || ''}
        busy={actionBusy}
      />
    </>,
  );
}

export default MarketPlaceDashboardView;
